/**
 * PayStack Payment Callback Page
 * Handles the redirect after PayStack payment
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaystackCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference');
      const trxref = searchParams.get('trxref');
      
      // PayStack passes the reference as 'trxref' parameter
      const paymentReference = reference || trxref;
      
      if (!paymentReference) {
        setStatus('error');
        setMessage('No payment reference found');
        toast.error('Payment verification failed');
        setTimeout(() => navigate('/dashboard'), 3000);
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/payments/paystack/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reference: paymentReference
          })
        });

        if (!response.ok) {
          throw new Error('Verification failed');
        }

        const result = await response.json();
        
        if (result.data.payment_status === 'completed') {
          setStatus('success');
          setMessage('Payment successful! Your account has been upgraded.');
          toast.success('Payment received. Your subscription is up to date. 🎉');
          
          // Redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate('/dashboard');
            window.location.reload(); // Refresh to update subscription status
          }, 2000);
        } else {
          throw new Error('Payment not completed');
        }
      } catch (error) {
        logger.error('Payment verification error:', error);
        setStatus('error');
        setMessage('Payment verification failed. Please contact support if amount was deducted.');
        toast.error('Payment verification failed');
        
        setTimeout(() => navigate('/dashboard'), 5000);
      }
    };

    verifyPayment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-linear-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="mb-6">
              <Loader2 className="h-16 w-16 animate-spin text-purple-600 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Verifying Payment
            </h2>
            <p className="text-slate-600">
              Please wait while we confirm your payment...
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="mb-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Successful!
            </h2>
            <p className="text-slate-600">
              {message}
            </p>
            <p className="text-sm text-slate-500 mt-4">
              Redirecting you to dashboard...
            </p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="mb-6">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Verification Failed
            </h2>
            <p className="text-slate-600 mb-4">
              {message}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-6 rounded-lg transition"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaystackCallback;
